import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaExclamationCircle, FaInfoCircle, FaTimes, FaWhatsapp, FaUsers, FaChartLine, FaCreditCard } from "react-icons/fa";
import api from "../lib/api";
import "./CSS/AdminPremium.css";

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "warning" || notice.type === "error"
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`admin-toast ${notice.type}`}
      role={notice.type === "warning" || notice.type === "error" ? "alert" : "status"}
      aria-live={notice.type === "warning" || notice.type === "error" ? "assertive" : "polite"}
    >
      <div className="admin-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="admin-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="admin-toast-close" type="button" onClick={onClose} aria-label="Close">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}
function StatCard({ tone, icon: Icon, label, value, caption }) {
  return (
    <article className={`premium-summary-card ${tone}`}>
      <div className="premium-summary-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="premium-summary-copy">
        <span>{label}</span>
        <strong>{value}</strong>
        <em>{caption}</em>
      </div>
    </article>
  );
}

export default function WhatsappSummarySettings() {
  const token = localStorage.getItem("token");

  const authHeaders = {
    Authorization: `Bearer ${token}`,
  };

  const [activeTab, setActiveTab] = useState("daily");
  const [notice, setNotice] = useState(null);

  const [dailySettings, setDailySettings] = useState({
    is_enabled: false,
    send_time: "13:00:00",
    timezone: "Asia/Kolkata",
    summary_mode: "previous_day",
    template_name: "fuelflow_daily_summary",
  });

  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState({
    recipient_name: "",
    phone: "",
    is_primary_recipient: false,
  });

  const [dailyPreviewDate, setDailyPreviewDate] = useState("");
  const [dailyPreview, setDailyPreview] = useState(null);
  const [dailyLogs, setDailyLogs] = useState([]);
  const [dailySending, setDailySending] = useState(false);
  const [dailySaving, setDailySaving] = useState(false);

  const [creditSettings, setCreditSettings] = useState({
    is_enabled: false,
    send_time: "20:00:00",
    timezone: "Asia/Kolkata",
    send_mode: "same_day",
    template_name: "fuelflow_customer_credit_summary",
    language_code: "en",
  });

  const [creditPreviewDate, setCreditPreviewDate] = useState("");
  const [creditPreview, setCreditPreview] = useState(null);
  const [creditLogs, setCreditLogs] = useState([]);
  const [creditSending, setCreditSending] = useState(false);
  const [creditSaving, setCreditSaving] = useState(false);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(() => setNotice(null), notice.type === "warning" || notice.type === "error" ? 5000 : 3200);
    return () => clearTimeout(timer);
  }, [notice]);

  const loadDailyData = async () => {
    const [settingsRes, logsRes] = await Promise.all([
      api.get("/api/whatsapp-summary/settings", { headers: authHeaders }),
      api.get("/api/whatsapp-summary/logs", { headers: authHeaders }),
    ]);

    setDailySettings(
      settingsRes.data.settings || {
        is_enabled: false,
        send_time: "13:00:00",
        timezone: "Asia/Kolkata",
        summary_mode: "previous_day",
        template_name: "fuelflow_daily_summary",
      }
    );
    setRecipients(settingsRes.data.recipients || []);
    setDailyLogs(logsRes.data || []);
  };

  const loadCreditData = async () => {
    const [settingsRes, logsRes] = await Promise.all([
      api.get("/api/customer-credit-whatsapp/settings", { headers: authHeaders }),
      api.get("/api/customer-credit-whatsapp/logs", { headers: authHeaders }),
    ]);

    setCreditSettings(
      settingsRes.data?.settings || {
        is_enabled: false,
        send_time: "20:00:00",
        timezone: "Asia/Kolkata",
        send_mode: "same_day",
        template_name: "fuelflow_customer_credit_summary",
        language_code: "en",
      }
    );
    setCreditLogs(logsRes.data || []);
  };

  const loadData = async () => {
    try {
      await Promise.all([loadDailyData(), loadCreditData()]);
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Load failed", message: "Failed to load WhatsApp configuration" });
    }
  };

  useEffect(() => {
    loadData();
   
  }, []);

  const saveDailySettings = async () => {
    try {
      setDailySaving(true);
      await api.put("/api/whatsapp-summary/settings", dailySettings, {
        headers: authHeaders,
      });
      setNotice({ id: Date.now(), type: "success", title: "Saved", message: "Daily summary settings saved" });
      await loadDailyData();
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Save failed", message: err?.response?.data?.error || "Failed to save daily summary settings" });
    } finally {
      setDailySaving(false);
    }
  };

  const addRecipient = async () => {
    try {
      if (!newRecipient.phone) {
        setNotice({ id: Date.now(), type: "warning", title: "Phone required", message: "Please enter a recipient phone number." });
        return;
      }

      await api.post("/api/whatsapp-summary/recipients", newRecipient, {
        headers: authHeaders,
      });

      setNewRecipient({
        recipient_name: "",
        phone: "",
        is_primary_recipient: false,
      });

      await loadDailyData();
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Add failed", message: err?.response?.data?.error || "Failed to add recipient" });
    }
  };

  const deleteRecipient = async (id) => {
    try {
      await api.delete(`/api/whatsapp-summary/recipients/${id}`, {
        headers: authHeaders,
      });
      await loadDailyData();
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Delete failed", message: err?.response?.data?.error || "Failed to delete recipient" });
    }
  };

  const fetchDailyPreview = async () => {
    try {
      const res = await api.get("/api/whatsapp-summary/preview", {
        headers: authHeaders,
        params: dailyPreviewDate ? { summary_date: dailyPreviewDate } : {},
      });
      setDailyPreview(res.data);
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Preview failed", message: err?.response?.data?.error || "Failed to load daily summary preview" });
    }
  };

  const sendDailyManual = async () => {
    try {
      setDailySending(true);
      const res = await api.post(
        "/api/whatsapp-summary/send-manual",
        { summary_date: dailyPreviewDate || null },
        { headers: authHeaders }
      );

      if (res.data.skipped) {
        setNotice({ id: Date.now(), type: "warning", title: "Skipped", message: `Skipped: ${res.data.reason}` });
      } else {
        setNotice({ id: Date.now(), type: "success", title: "Sent", message: "Daily summary sent successfully" });
      }

      await loadDailyData();
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Send failed", message: err?.response?.data?.error || "Failed to send daily summary" });
    } finally {
      setDailySending(false);
    }
  };

  const saveCreditSettings = async () => {
    try {
      setCreditSaving(true);
      await api.put("/api/customer-credit-whatsapp/settings", creditSettings, {
        headers: authHeaders,
      });
      setNotice({ id: Date.now(), type: "success", title: "Saved", message: "Customer credit WhatsApp settings saved" });
      await loadCreditData();
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Save failed", message: err?.response?.data?.error || "Failed to save customer credit WhatsApp settings" });
    } finally {
      setCreditSaving(false);
    }
  };

  const fetchCreditPreview = async () => {
    try {
      const res = await api.get("/api/customer-credit-whatsapp/preview", {
        headers: authHeaders,
        params: creditPreviewDate ? { summary_date: creditPreviewDate } : {},
      });
      setCreditPreview(res.data);
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Preview failed", message: err?.response?.data?.error || "Failed to load customer credit WhatsApp preview" });
    }
  };

  const sendCreditManual = async () => {
    try {
      setCreditSending(true);
      const res = await api.post(
        "/api/customer-credit-whatsapp/send-manual",
        { summary_date: creditPreviewDate || null },
        { headers: authHeaders }
      );

      if (res.data.skipped) {
        setNotice({ id: Date.now(), type: "warning", title: "Skipped", message: `Skipped: ${res.data.reason}` });
      } else {
        setNotice({ id: Date.now(), type: "success", title: "Sent", message: "Customer credit WhatsApp sent successfully" });
      }

      await loadCreditData();
    } catch (err) {
      console.error(err);
      setNotice({ id: Date.now(), type: "error", title: "Send failed", message: err?.response?.data?.error || "Failed to send customer credit WhatsApp" });
    } finally {
      setCreditSending(false);
    }
  };

  const stats = {
    recipients: recipients.length,
    dailyLogs: dailyLogs.length,
    creditLogs: creditLogs.length,
    activeMode: activeTab === "daily" ? "Daily Summary" : "Customer Credit",
  };

  return (
    <div className="admin-premium-page whatsapp-page">
      <section className="premium-hero">
        <div>
          <span className="premium-eyebrow">Automation</span>
          <h1>WhatsApp Configuration</h1>
          <p>Configure daily summary and customer credit WhatsApp from one premium control center.</p>
        </div>
        <div className="premium-tab-strip">
          <button
            type="button"
            className={`btn ${activeTab === "daily" ? "btn-success" : "btn-outline-secondary"}`}
            onClick={() => setActiveTab("daily")}
          >
            Daily Summary
          </button>
          <button
            type="button"
            className={`btn ${activeTab === "credit" ? "btn-success" : "btn-outline-secondary"}`}
            onClick={() => setActiveTab("credit")}
          >
            Customer Credit
          </button>
        </div>
      </section>

      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="premium-summary-grid">
        <StatCard tone="blue" icon={FaUsers} label="Recipients" value={stats.recipients} caption="Configured WhatsApp recipients" />
        <StatCard tone="green" icon={FaChartLine} label="Daily Logs" value={stats.dailyLogs} caption="Daily summary history" />
        <StatCard tone="amber" icon={FaCreditCard} label="Credit Logs" value={stats.creditLogs} caption="Customer credit history" />
        <StatCard tone="red" icon={FaWhatsapp} label="Mode" value={stats.activeMode} caption="Currently selected workflow" />
      </section>

      <section className="premium-context-strip">
        <div>
          <span className="premium-eyebrow" style={{ marginBottom: 8 }}>Current tab</span>
          <strong style={{ display: "block", fontSize: "1rem", fontWeight: 950, color: "#0f172a" }}>
            {activeTab === "daily" ? "Daily Summary" : "Customer Credit WhatsApp"}
          </strong>
          <p style={{ margin: "6px 0 0", color: "rgba(15,23,42,0.66)", fontSize: "0.92rem" }}>
            {activeTab === "daily"
              ? "Manage recipients, preview, and send daily station summaries."
              : "Configure and preview customer credit notifications."}
          </p>
        </div>
        <div className="premium-context-chip">
          <span>Timezone</span>
          <strong>Asia/Kolkata</strong>
        </div>
      </section>

      {activeTab === "daily" && (
        <>
          <div className="premium-card whatsapp-section mb-3">
            <div className="card-body">
              <h5 className="mb-3">Daily Summary Settings</h5>

              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Enable</label>
                  <select
                    className="form-select"
                    value={dailySettings.is_enabled ? "1" : "0"}
                    onChange={(e) =>
                      setDailySettings({
                        ...dailySettings,
                        is_enabled: e.target.value === "1",
                      })
                    }
                  >
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Send Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={(dailySettings.send_time || "13:00:00").slice(0, 5)}
                    onChange={(e) =>
                      setDailySettings({
                        ...dailySettings,
                        send_time: `${e.target.value}:00`,
                      })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Timezone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={dailySettings.timezone || "Asia/Kolkata"}
                    onChange={(e) =>
                      setDailySettings({
                        ...dailySettings,
                        timezone: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Summary Mode</label>
                  <select
                    className="form-select"
                    value={dailySettings.summary_mode || "previous_day"}
                    onChange={(e) =>
                      setDailySettings({
                        ...dailySettings,
                        summary_mode: e.target.value,
                      })
                    }
                  >
                    <option value="previous_day">Previous Day</option>
                    <option value="same_day">Same Day</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Template Name</label>
                  <input
  type="text"
  className="form-control"
  value="fuelflow_reconciliation_alert"
  disabled
/>
                </div>

                <div className="col-md-12">
                  <button
                    className="btn btn-success"
                    onClick={saveDailySettings}
                    disabled={dailySaving}
                  >
                    {dailySaving ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="premium-card whatsapp-section mb-3">
            <div className="card-body">
              <h5 className="mb-3">Recipients</h5>

              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Recipient Name"
                    value={newRecipient.recipient_name}
                    onChange={(e) =>
                      setNewRecipient({
                        ...newRecipient,
                        recipient_name: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-4">
                  <input
                    className="form-control"
                    placeholder="Phone"
                    value={newRecipient.phone}
                    onChange={(e) =>
                      setNewRecipient({
                        ...newRecipient,
                        phone: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-2">
                  <select
                    className="form-select"
                    value={newRecipient.is_primary_recipient ? "1" : "0"}
                    onChange={(e) =>
                      setNewRecipient({
                        ...newRecipient,
                        is_primary_recipient: e.target.value === "1",
                      })
                    }
                  >
                    <option value="0">Normal</option>
                    <option value="1">Primary</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <button className="btn btn-primary w-100" onClick={addRecipient}>
                    Add
                  </button>
                </div>
              </div>

              <div className="table-responsive premium-table-shell">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Phone</th>
                      <th>Primary</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.map((r) => (
                      <tr key={r.id}>
                        <td>{r.recipient_name || "-"}</td>
                        <td>{r.phone}</td>
                        <td>{r.is_primary_recipient ? "Yes" : "No"}</td>
                        <td>
                          <button
                            className="btn btn-sm btn-danger"
                            onClick={() => deleteRecipient(r.id)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!recipients.length && (
                      <tr>
                        <td colSpan="4" className="text-center">
                          No recipients added
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="premium-card whatsapp-section mb-3">
            <div className="card-body">
              <h5 className="mb-3">Preview & Send</h5>

              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Summary Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={dailyPreviewDate}
                    onChange={(e) => setDailyPreviewDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4 d-flex align-items-end whatsapp-action-row">
                  <button className="btn btn-outline-info me-2" onClick={fetchDailyPreview}>
                    Preview
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={sendDailyManual}
                    disabled={dailySending}
                  >
                    {dailySending ? "Sending..." : "Send Now"}
                  </button>
                </div>
              </div>

              {dailyPreview && (
                <div className="bg-black p-3 rounded">
                  <div><strong>Pump:</strong> {dailyPreview.tenantName}</div>
                  <div><strong>Date:</strong> {dailyPreview.summaryDate}</div>
                  <div><strong>Recon Count:</strong> {dailyPreview.reconCount}</div>
                  <hr />
                  <pre style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                    {dailyPreview.preview_text}
                  </pre>
                </div>
              )}
            </div>
          </div>

          <div className="premium-card whatsapp-section">
            <div className="card-body">
              <h5 className="mb-3">Daily Summary Logs</h5>

              <div className="table-responsive premium-table-shell">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Trigger</th>
                      <th>Status</th>
                      <th>Recipients</th>
                      <th>Sent At</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.summary_date}</td>
                        <td>{log.trigger_source}</td>
                        <td>{log.status}</td>
                        <td>{log.recipient_count}</td>
                        <td>{log.sent_at || "-"}</td>
                        <td>{log.error_message || "-"}</td>
                      </tr>
                    ))}
                    {!dailyLogs.length && (
                      <tr>
                        <td colSpan="6" className="text-center">
                          No logs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}

      {activeTab === "credit" && (
        <>
          <div className="premium-card whatsapp-section mb-3">
            <div className="card-body">
              <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
                <div>
                  <h5 className="mb-1">Customer Credit WhatsApp Settings</h5>
                  <div className="text-secondary small">
                    Customer phone will be picked automatically from the users table based on customer_id
                  </div>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">Enable</label>
                  <select
                    className="form-select"
                    value={creditSettings.is_enabled ? "1" : "0"}
                    onChange={(e) =>
                      setCreditSettings({
                        ...creditSettings,
                        is_enabled: e.target.value === "1",
                      })
                    }
                  >
                    <option value="1">Enabled</option>
                    <option value="0">Disabled</option>
                  </select>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Send Time</label>
                  <input
                    type="time"
                    className="form-control"
                    value={(creditSettings.send_time || "20:00:00").slice(0, 5)}
                    onChange={(e) =>
                      setCreditSettings({
                        ...creditSettings,
                        send_time: `${e.target.value}:00`,
                      })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Timezone</label>
                  <input
                    type="text"
                    className="form-control"
                    value={creditSettings.timezone || "Asia/Kolkata"}
                    onChange={(e) =>
                      setCreditSettings({
                        ...creditSettings,
                        timezone: e.target.value,
                      })
                    }
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label">Send Mode</label>
                  <select
                    className="form-select"
                    value={creditSettings.send_mode || "same_day"}
                    onChange={(e) =>
                      setCreditSettings({
                        ...creditSettings,
                        send_mode: e.target.value,
                      })
                    }
                  >
                    <option value="same_day">Same Day</option>
                    <option value="previous_day">Previous Day</option>
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Template Name</label>
                  <input
  type="text"
  className="form-control"
  value="customer_credit_summary"
  disabled
/>
                </div>

                <div className="col-md-3">
                  <label className="form-label">Language Code</label>
                  <input
  type="text"
  className="form-control"
  value="en"
  disabled
/>
                </div>

                <div className="col-md-12">
                  <button
                    className="btn btn-success"
                    onClick={saveCreditSettings}
                    disabled={creditSaving}
                  >
                    {creditSaving ? "Saving..." : "Save Settings"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="premium-card whatsapp-section mb-3">
            <div className="card-body">
              <h5 className="mb-3">Customer Credit Preview & Send</h5>

              <div className="row g-2 mb-3">
                <div className="col-md-4">
                  <label className="form-label">Summary Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={creditPreviewDate}
                    onChange={(e) => setCreditPreviewDate(e.target.value)}
                  />
                </div>

                <div className="col-md-4 d-flex align-items-end whatsapp-action-row">
                  <button className="btn btn-outline-info me-2" onClick={fetchCreditPreview}>
                    Preview
                  </button>
                  <button
                    className="btn btn-success"
                    onClick={sendCreditManual}
                    disabled={creditSending}
                  >
                    {creditSending ? "Sending..." : "Send Now"}
                  </button>
                </div>
              </div>

              {creditPreview && (
                <div className="bg-black p-3 rounded">
                  <div><strong>Pump:</strong> {creditPreview.tenantName || "-"}</div>
                  <div><strong>Date:</strong> {creditPreview.summaryDate || "-"}</div>
                  <div><strong>Total Customers:</strong> {creditPreview.totalCustomers || 0}</div>
                  <hr />
                  <pre style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}>
                    {creditPreview.preview_text ||
                      JSON.stringify(creditPreview, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          </div>

          <div className="premium-card whatsapp-section">
            <div className="card-body">
              <h5 className="mb-3">Customer Credit WhatsApp Logs</h5>

              <div className="table-responsive premium-table-shell">
                <table className="table table-hover align-middle">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Customer</th>
                      <th>Phone</th>
                      <th>Trigger</th>
                      <th>Status</th>
                      <th>Sent At</th>
                      <th>Error</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creditLogs.map((log) => (
                      <tr key={log.id}>
                        <td>{log.summary_date}</td>
                        <td>{log.recipient_name || "-"}</td>
                        <td>{log.recipient_phone || "-"}</td>
                        <td>{log.trigger_source}</td>
                        <td>{log.status}</td>
                        <td>{log.sent_at || "-"}</td>
                        <td>{log.error_message || "-"}</td>
                      </tr>
                    ))}
                    {!creditLogs.length && (
                      <tr>
                        <td colSpan="7" className="text-center">
                          No customer credit logs found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}



